import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  UncontrolledDropdown,
} from 'reactstrap';

import http from 'src/services/httpService';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import Table from 'src/componentes/table';
import Pagination from 'src/componentes/list/pagination';
import Confirm from 'src/componentes/message-box/confirm';
import errorHandler from 'src/utils/error-handler';
import { Checkbox } from 'src/componentes/Form';
import guid from 'src/utils/guid';
import Info from 'src/componentes/info';
import LucideIcon from 'src/componentes/lucide-icon';

const stickyDefault = {
  position: 'sticky',
  background: 'inherit',
  zIndex: 5,
};

const colorIcon = '#939393';

const ActionMenuIcon = ({ children }) => (
  <span
    className="d-inline-flex align-items-center justify-content-center"
    style={{
      height: 24,
      lineHeight: 0,
      verticalAlign: 'middle',
      width: 20,
    }}
  >
    {children}
  </span>
);

function Results({
  resources,
  count,
  columns,
  showPagination,
  currentPage,
  pageSize,
  onChangeCurrentPage,
  onChangePageSize,
  onSortColumn,
  onSortByColumn,
  forceShowSort = false,
  sort,
  sortBy,
  data,
  url /** Caminho para Controller, com '/' no início */,
  updatedItems,
  showDelete = true,
  parameters,
  onRefresh,
  handleRemove,
  onRemove,
  handleEditClick,
  showMenu,
  expandedIds,
  loadingChildrenIds,
  isExpanded,
  handleToggleExpand,
  select = false,
  selectLimit,
  onSelect,
  onDeleting,
  responsive = true,
  sticky = false,
  selectedItems = [],
  getRowColor = () => {},
  getRowTextColor = () => {},
  renderFakeChildrenWhileLoading,
  renderContentWhileLoading,
  renderMenu: renderMenuProps,
  showSortIcon = true,
  isLoading = false,
  dragTable = false,
  showFullTextColumns = false,
  multiple = false,
  idLabelEditar,
  idLabelExcluir,
  showScrollColumn = true,
  propsPopOver = {},
  handleRefreshPage,
  dataTotalRow,
  selectedCallback,
  checkSelected = false,
  showSelectAll = true,
}) {
  const intl = useIntl();
  const [allItensSelected, setAllItensSelected] = useState(false);
  const [currentSelectedItems, setCurrentSelectedItems] = useState([
    ...(selectedItems || []),
  ]);

  useEffect(() => {
    if (select && onSelect) {
      onSelect([...(currentSelectedItems || [])]);
    }

    if (selectedCallback) selectedCallback([...(currentSelectedItems || [])]);
  }, [currentSelectedItems]);

  const getCellPopoVer = (item, column) => {
    if (column.popoverFunction) {
      return column.popoverFunction(item, propsPopOver);
    }
  };

  const handleHeaderClick = (column) => {
    if (!column.sortField) return;

    const sortByKey = sortBy === 'asc' ? 'desc' : 'asc';

    onSortColumn && onSortColumn(column.sortField, sortByKey);
    onSortByColumn && onSortByColumn(sortByKey);
  };

  const getHeaderText = (column) => {
    if (column.resource) {
      if (resources) {
        return resources[column.resource];
      } else {
        return column.customHeaderText ?? column.headerText;
      }
    } else {
      return column.customHeaderText ?? column.headerText;
    }
  };

  const getCellContent = (item, column) => {
    if (column.valueFunction) {
      return column.valueFunction(item, propsPopOver, column);
    }

    if (column.valueField) {
      let value = item[column.valueField];

      if (column.formatFunction) {
        value = column.formatFunction(value);
      }

      if (item.campoCustomizado) {
        if (item.campoCustomizado.length > 0) {
          let campoCustomizado = item.campoCustomizado.find(
            (i) => i.nomeCampo === column.valueField
          );
          if (campoCustomizado) value = campoCustomizado.valor;
        }
      }

      return value;
    }
  };

  const isUpdated = (item) => {
    return updatedItems.find((i) => i.id === item.id);
  };

  const handleDeleteClickConfirm = async (item, index) => {
    if (handleRemove) handleRemove && handleRemove(item, index);
    else
      try {
        await http.post(`${url}/Remove`, { id: item.id, parameters });
        ContentManager.addContent(
          <Alert
            title={<FormattedMessage id="sucesso" />}
            message={<FormattedMessage id="label.registroExcluidoComSucesso" />}
          />
        );
        onRefresh && onRefresh();
        onRemove && onRemove();
      } catch (error) {
        errorHandler(error);
      }
  };

  const handleDeleteClick = (item, index) => {
    if (onDeleting) {
      onDeleting(item);
    } else {
      ContentManager.addContent(
        <Confirm
          message={
            <FormattedMessage id="label.desejaMesmoExcluirEsseRegistro" />
          }
          handleConfirm={() => handleDeleteClickConfirm(item, index)}
        />
      );
    }
  };

  const renderMenu = (item, indexItem) => {
    return (
      <UncontrolledDropdown className="results-table-dropdown">
        <DropdownToggle
          tag="a"
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            justifyContent: 'center',
            minHeight: 35,
            padding: 0,
          }}
        >
          <ActionMenuIcon>
            <LucideIcon icon="EllipsisVertical" size={18} />
          </ActionMenuIcon>
        </DropdownToggle>
        <DropdownMenu container="body" placement="left">
          {item.permissions && (
            <DropdownItem
              onClick={() =>
                handleEditClick(item, indexItem, parameters, onRefresh)
              }
            >
              <LucideIcon icon="Pencil" className="mr-2" size={16} />
              {intl.formatMessage(
                { id: idLabelEditar ? idLabelEditar : 'label.editar' },
                { editar: resources.editar }
              )}
            </DropdownItem>
          )}
          {item.permissions && item.permissions.allowDelete && showDelete && (
            <DropdownItem onClick={() => handleDeleteClick(item, indexItem)}>
              <LucideIcon icon="Trash2" className="mr-2" size={16} />
              <FormattedMessage
                id={idLabelExcluir ? idLabelExcluir : 'excluir'}
              />
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  const checkCanSelected = (item) => {
    return !checkSelected || item.canSelected;
  };

  const handleToggleItem = (item) => {
    let newList = [];

    if (multiple) {
      newList = Object.assign([], currentSelectedItems);

      const index = newList.findIndex((x) => x.id === item.id);

      if (index === -1) newList.push(item);
      else newList.splice(index, 1);
    } else {
      newList.push(item);
    }

    setAllItensSelected(newList?.length === count);
    setCurrentSelectedItems(newList);
  };

  const handleToggleSelectAll = () => {
    if (!allItensSelected && data?.length > 0) {
      setCurrentSelectedItems([...data]);
    } else setCurrentSelectedItems([]);

    setAllItensSelected(!allItensSelected);
  };

  const isSelected = (id) => {
    return currentSelectedItems?.find((x) => x.id === id);
  };

  const renderFakeRows = (item, level) =>
    renderFakeChildrenWhileLoading(item.id, level * 20);

  const getStyleCell = (index) => {
    const stickyObj = {
      ...stickyDefault,
    };

    if (index === 0 && sticky) {
      stickyObj['left'] = 0;
      return stickyObj;
    }

    return {};
  };

  const getStyleCellBottom = (index) => {
    const stickyObj = {
      ...stickyDefault,
    };

    if (sticky) {
      if (index === 0) {
        stickyObj['zIndex'] = 6;
        stickyObj['left'] = 0;
      }

      stickyObj['bottom'] = 0;
      stickyObj['background'] = '#FFFFFF';
      stickyObj['fontWeight'] = 'bold';
      stickyObj['border'] = 'none';
      stickyObj['boxShadow'] = 'inset 0 2px 0 #dee2e6, inset 0 -2px 0 #dee2e6';
      return stickyObj;
    }

    return {};
  };

  const renderRows = (data, level = 0, parentColor = null) => {
    if (!data) return;

    return data.map((item, indexItem) => {
      // se for adm (usuario externo) nao tem 'permissions', então renderiza sempre o menu, se for interno, verifica se tem permissão de editar ou visualizar
      const canRenderRowMenu =
        !item?.permissions ||
        item.permissions?.allowEdit ||
        item.permissions?.allowView;
      const currentBackgroundColor =
        getRowColor && getRowColor(indexItem, parentColor);
      const currentTextColor =
        getRowTextColor && getRowTextColor(indexItem, parentColor);
      return [
        <tr
          key={item.id}
          style={{
            color: currentTextColor || 'inherit',
            backgroundColor: !currentBackgroundColor
              ? isUpdated(item)
                ? '#ECECEC'
                : '#FFFFFF'
              : currentBackgroundColor,
          }}
        >
          {showMenu && (
            <td style={{ zIndex: 1, position: 'relative' }}>
              {canRenderRowMenu &&
                (renderMenuProps
                  ? renderMenuProps(item, onRefresh)
                  : renderMenu(item, indexItem))}
            </td>
          )}
          {select && (
            <td style={{ width: 30 }}>
              {checkCanSelected(item) && (
                <Checkbox
                  id={`toggle-individual-item-${guid()}`}
                  key={guid()}
                  disabled={
                    selectLimit &&
                    currentSelectedItems?.length >= selectLimit &&
                    !isSelected(item.id)
                  }
                  checked={isSelected(item.id)}
                  onChange={() => handleToggleItem(item)}
                />
              )}
            </td>
          )}
          {columns.map(
            (column, index) =>
              column?.visible && (
                <td
                  id={'col' + column.valueField + item.id}
                  key={index}
                  className={classnames('results-table-cell', column.className)}
                  style={{ ...column.style, ...getStyleCell(index) }}
                >
                  <div
                    className={`d-flex m-auto align-items-center ${
                      !column.tree && showScrollColumn
                        ? 'overflow-y-auto overflow-x-hidden'
                        : !column.tree && !showScrollColumn
                        ? 'overflow-y-hidden overflow-x-hidden'
                        : ''
                    }`}
                    style={{
                      paddingLeft: column.tree ? level * 25 : 0,
                      maxHeight: column.maxHeight
                        ? column.maxHeight
                        : !column.tree
                        ? 130
                        : '',
                      minHeight: column.tree ? 25 : 'auto',
                      maxWidth: column.maxWidth ? column.maxWidth : 'auto',
                      minWidth: column.minWidth ? column.minWidth : 100,
                      wordWrap: 'anywhere',
                    }}
                  >
                    {column.tree &&
                      (item.hasChildren ? (
                        <>
                          {isExpanded(item) ? (
                            <ChevronDown
                              className="results-table-hierarchy"
                              color={colorIcon}
                              onClick={() => handleToggleExpand(item)}
                            />
                          ) : (
                            <ChevronRight
                              className="results-table-hierarchy"
                              color={colorIcon}
                              onClick={() => handleToggleExpand(item)}
                            />
                          )}
                        </>
                      ) : (
                        <span
                          className="results-table-hierarchy"
                          style={{ display: 'inline-block' }}
                        />
                      ))}
                    <div
                      style={{
                        maxHeight: column.maxHeight ? column.maxHeight : '',
                      }}
                    >
                      {getCellContent(item, column)}
                      {getCellPopoVer(item, column)}
                    </div>
                  </div>
                </td>
              )
          )}
        </tr>,
        expandedIds &&
          expandedIds.indexOf(item.id) !== -1 &&
          ((loadingChildrenIds
            ? loadingChildrenIds.includes(item.id)
            : !item.children?.length) && renderFakeChildrenWhileLoading
            ? renderFakeRows(item, level + 1)
            : renderRows(item.children, level + 1, currentBackgroundColor)),
      ];
    });
  };

  const renderTotalRow = (dataTotalRow) => {
    if (!dataTotalRow) return;

    return columns.map(
      (column, index) =>
        column?.visible && (
          <td
            id={'col' + column.valueField + 't'}
            key={index}
            className={classnames('results-table-cell', column.className)}
            style={{ ...column.style, ...getStyleCellBottom(index) }}
          >
            <div
              className={`d-flex m-auto align-items-center ${
                !column.tree && showScrollColumn
                  ? 'overflow-y-auto overflow-x-hidden'
                  : !column.tree && !showScrollColumn
                  ? 'overflow-y-hidden overflow-x-hidden'
                  : ''
              }`}
              style={{
                paddingLeft: 0,
                maxHeight: column.maxHeight
                  ? column.maxHeight
                  : !column.tree
                  ? 130
                  : '',
                minHeight: column.tree ? 25 : 'auto',
                maxWidth: column.maxWidth ? column.maxWidth : 'auto',
                minWidth: column.minWidth ? column.minWidth : 100,
                wordWrap: 'anywhere',
              }}
            >
              <div
                style={{
                  maxHeight: column.maxHeight ? column.maxHeight : '',
                }}
              >
                {getCellContent(dataTotalRow, column)}
                {getCellPopoVer(dataTotalRow, column)}
              </div>
            </div>
          </td>
        )
    );
  };

  const renderSort = (column) => {
    if (!forceShowSort && (!sort || !column.sortField)) return;

    let SortIcon = ArrowUpDown;

    if (column.sortField == sort?.split(' ')[0] && sortBy == 'desc') {
      SortIcon = ChevronUp;
    }
    if (column.sortField == sort?.split(' ')[0] && sortBy == 'asc') {
      SortIcon = ChevronDown;
    }

    return (
      <SortIcon style={{ cursor: 'pointer' }} size={14} className="ml-2" />
    );
  };

  const getStyleHeader = (index) => {
    const stickyObj = {
      ...stickyDefault,
      verticalAlign: 'top',
    };

    if (sticky) {
      stickyObj['top'] = -1;
      stickyObj['background'] = '#FFFFFF';
      stickyObj['border'] = 'none';
      stickyObj['boxShadow'] = 'inset 0 2px 0 #dee2e6, inset 0 -2px 0 #dee2e6';

      if (index === 0) {
        stickyObj['zIndex'] = 6;
        stickyObj['left'] = 0;
        return stickyObj;
      }

      return stickyObj;
    }

    return { verticalAlign: 'top' };
  };

  return (
    <div>
      {data && data.length > 0 ? (
        <Table
          drag={dragTable}
          responsive={responsive}
          styleDiv={{}}
          style={{ background: '#FFF', marginBottom: 0 }}
          count={count}
          stickyHeader={sticky}
        >
          <thead>
            <tr>
              {showMenu && (
                <th style={{ width: 30, ...getStyleHeader(-1) }}></th>
              )}
              {select && (
                <th style={{ width: 30, ...getStyleHeader(-1) }}>
                  {multiple && showSelectAll && (
                    <Checkbox
                      id="toggle-all"
                      disabled={selectLimit && count > selectLimit}
                      checked={allItensSelected}
                      onChange={handleToggleSelectAll}
                    />
                  )}
                </th>
              )}
              {columns.map(
                (column, index) =>
                  column?.visible && (
                    <th
                      key={index}
                      onClick={() => handleHeaderClick(column)}
                      className={classnames(
                        column.className,
                        showFullTextColumns ? 'text-nowrap' : ''
                      )}
                      style={{ ...column.styleHead, ...getStyleHeader(index) }}
                    >
                      <div className="d-flex align-items-center">
                        {getHeaderText(column)}
                        {showSortIcon && column.sortField && renderSort(column)}
                        {column.tooltipHeader && (
                          <Info
                            style={{ marginLeft: 5 }}
                            id={`hint-column-list-${index}`}
                            tooltipMaxWidth={500}
                            placement="auto"
                            text={column.tooltipHeader}
                          />
                        )}
                      </div>
                    </th>
                  )
              )}
            </tr>
          </thead>
          <tbody>
            {renderRows(data)}
            {dataTotalRow && <tr>{renderTotalRow(dataTotalRow)}</tr>}
            {isLoading &&
              renderContentWhileLoading &&
              renderContentWhileLoading()}
          </tbody>
        </Table>
      ) : (
        !isLoading && (
          <Table
            responsive={responsive}
            hover={false}
            styleDiv={{}}
            style={{ background: '#FFF', marginBottom: 0 }}
          >
            <tbody>
              <tr>
                <td style={{ borderTop: 0, padding: 0 }}>
                  <p style={{ fontStyle: 'italic', padding: 20 }}>
                    {intl.formatMessage({
                      id: 'label.nenhumRegistroFoiEncontrado',
                    })}
                  </p>
                </td>
              </tr>
            </tbody>
          </Table>
        )
      )}
      {showPagination && count > 0 && (
        <div className="d-flex justify-content-end p-3">
          <Pagination
            allowPaging
            currentPage={currentPage}
            count={count}
            pageSize={pageSize}
            changeCurrentPage={onChangeCurrentPage}
            changePageSize={onChangePageSize}
            onRefreshClick={handleRefreshPage}
          />
        </div>
      )}
    </div>
  );
}

Results.defaultProps = {
  showPagination: true,
  data: [],
  updatedItems: [],
};

export default Results;
